import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import { Dialog } from "antd-mobile";
import { ArrowLeft, Camera, Users, ChevronDown, ChevronUp, Check, Heart } from "lucide-react";
import favoriteMealService from "../../services/FavoriteMealService";
import ImageDosa from "../../assets/food.png";
import welcomeBg from "../../assets/homebg.png";

import { useUser } from "@/context/useUser";
import { useFoodManagement } from "../../hooks/useFoodManagement";
import homeService from "../../services/HomeService";
import {
  FoodItem as ApiFoodItem,
  foodApiService,
  EditFoodItemPayload,
  SaveMealPayload,
} from "../../services/foodApiService";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import { buildEditFoodPayload, stripCountFromServingDescription } from "../../utils/foodEditPayload";
import PredictedGlucoseSpikeCard from "../../components/HomePage/PredictedGlucoseSpikeCard";
import MedicalDisclaimerNotice from "../../components/MedicalDisclaimerNotice";
import NutritionFactsCard from "../../components/common/NutritionFactsCard";
import FoodItemsList from "./components/FoodItemsList";
import EditFoodModal from "./components/EditFoodModal";
import AddFoodModal from "./components/AddFoodModal";
import PlateOptimisationCard, { PlateOptimisationData } from "../../components/common/PlateOptimisationCard";
import FamilyService from "../../services/FamilyService";
import { cn } from "../../lib/utils";
import {
  gluceraFootnoteBody,
  gluceraFormControl,
  gluceraFieldLabel,
  gluceraMicroLabel,
  gluceraPrimaryCta,
  gluceraReviewCaption,
  gluceraReviewTitle,
  gluceraScoreDisplay,
  gluceraScoreEyebrow,
  gluceraSectionTitle,
} from "../../utils/gluceraTypography";
import {
  buildFoodAnalyzeDataForSave,
  buildLogForUserIds,
  buildSelectedFamilyGroups,
  readMealTitleFromAnalyzePayload,
  resolveMealTitleForSave,
} from "../../utils/mealSavePayload";
import { readMealGiGlFromAnalyze } from "../../utils/foodResponseParser";

interface FoodItem {
  id: number;
  food_item_id?: string; // API food item ID
  name: string;
  csv_name?: string; // Backend field: normalized name
  source?: string; // Backend field: llm, csv, etc.
  count?: number; // Backend field: item count
  /** Immutable snapshot from first predict/scan API — used in edit payload for backend */
  predicted_count?: number;
  predicted_serving_size_g?: number;
  predicted_serving_description?: string;
  unit_label?: string;
  predicted_unit_label?: string;
  calories: number;
  calorie_content_kcal?: number; // API field for calories
  weight: number;
  servings: number;
  image: string;
  // API nutrition data
  carbohydrate_content_g?: number;
  protein_content_g?: number;
  fat_content_g?: number;
  dietary_fiber_g?: number;
  net_carbs_g?: number;
  sugar_g?: number;
  trans_fat_g?: number;
  saturated_fat_g?: number;
  GI_estimate?: number;
  GL_estimate?: number;
  serving_description?: string;
}

interface NutritionTotals {
  carbohydrate_content_g: number;
  dietary_fiber_g: number;
  net_carbs_g: number;
  sugar_g: number;
  protein_content_g: number;
  fat_content_g: number;
  trans_fat_g: number;
  saturated_fat_g: number;
  calorie_content_kcal: number;
  GI_estimate: number;
  GL_estimate: number;
}

const SCANNED_DETAIL_MEAL_TYPES = ["Breakfast", "Lunch", "Dinner", "Snack"] as const;

/** meal_type from analyze_food_image cached JSON (plain API body or axios-shaped `{ data: body }`). */
function readMealTypeFromAnalyzePayload(parsed: Record<string, unknown> | null): string | null {
  if (!parsed) return null;
  const data = parsed.data as Record<string, unknown> | undefined;
  const nested = data?.data as Record<string, unknown> | undefined;
  const result = parsed.result as Record<string, unknown> | undefined;
  for (const c of [
    parsed.meal_type,
    data?.meal_type,
    nested?.meal_type,
    result?.meal_type,
  ]) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return null;
}

function toMealTypeSelectValue(raw: string | null): string {
  if (!raw) return "Breakfast";
  const lower = raw.trim().toLowerCase();
  const match = SCANNED_DETAIL_MEAL_TYPES.find(t => t.toLowerCase() === lower);
  return match ?? "Breakfast";
}

/** One-line headline from the selected meal type (aligned with API meal_type). */
function lookLikeMealHeadline(mealType: string): string {
  const match =
    SCANNED_DETAIL_MEAL_TYPES.find(
      t => t.toLowerCase() === mealType.trim().toLowerCase()
    ) ?? "Breakfast";
  if (match === "Snack") return "Looks like a snack.";
  return `Looks like ${match.toLowerCase()}.`;
}

const ScannedFooddetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { decodedToken } = useUser();
  const { uploadedImage, foodItemsFromBackend, documentId, ghiReasons, setGhiReasons, ghiScore, setGhiScore, ghiLabel, setGhiLabel, bbsScore, setBbsScore, bbsLabel, setBbsLabel, otherDetails, setOtherDetails, glucoseSpikeData, setGlucoseSpikeData } =
    useFoodManagement();

  // Get meal number from navigation state
  const mealNumber = location.state?.mealNumber ?? 1;
  const fromHomePage = location.state?.fromHomePage ?? false;

  // Handle back navigation - Always go to home page for complete flow
  const handleBackNavigation = () => {
    // Always navigate to home page for complete flow
    navigate(`/glucera${GluceraNavigationRoutes.HOMEPAGE}`, {
      state: {
        mealNumber: mealNumber,
        fromScannedDetail: true,
      },
    });
  };

  // State for uploaded image with localStorage persistence
  const [currentUploadedImage, setCurrentUploadedImage] = useState<
    string | null
  >(() => {
    // Try to load image from localStorage first
    const savedImage = localStorage.getItem("glucera_uploaded_image");

    // Prefer saved base64 data URL over temporary blob URL
    if (savedImage?.startsWith("data:")) {
      return savedImage;
    } else if (savedImage?.startsWith("blob:")) {
      // Check if blob URL is still valid (though this is unlikely to work after page refresh)
      return savedImage;
    } else if (savedImage?.startsWith("http")) {
      return savedImage;
    } else if (uploadedImage) {
      return uploadedImage;
    }

    return null;
  });

  // Parse leading number from serving_description e.g. "2 whole" → 2, "15 pieces" → 15
  const parseServingsFromDescription = (desc?: string, apiCount?: number): number => {
    if (apiCount && apiCount > 0) return apiCount;
    if (!desc) return 1;
    const match = desc.match(/^(\d+(?:\.\d+)?)\s/);
    const parsed = match ? parseFloat(match[1]) : 1;
    return parsed > 0 ? parsed : 1;
  };

  // Convert API food items to local format
  const convertApiFoodItems = useCallback(
    (apiItems: ApiFoodItem[]): FoodItem[] => {
      return apiItems.map((item, index) => {
        const predictedCount =
          (item as any).count ??
          parseServingsFromDescription(item.serving_description, (item as any).count);
        const unitLabel =
          (item as any).unit_label ??
          (stripCountFromServingDescription(item.serving_description) ||
            "serving");
        return {
        id: index + 1,
        food_item_id: (item as any).food_item_id, // API items may have food_item_id
        name: item.name,
        csv_name: (item as any).csv_name, // Backend field
        source: (item as any).source, // Backend field
        count: predictedCount,
        predicted_count: predictedCount,
        predicted_serving_size_g: item.serving_size_g,
        predicted_serving_description: item.serving_description,
        unit_label: unitLabel,
        predicted_unit_label: unitLabel,
        calories: item.calorie_content_kcal,
        weight: item.serving_size_g,
        servings: parseServingsFromDescription(item.serving_description, (item as any).count),
        image: currentUploadedImage ?? uploadedImage ?? ImageDosa,
        carbohydrate_content_g: item.carbohydrate_content_g,
        protein_content_g: item.protein_content_g,
        fat_content_g: item.fat_content_g,
        dietary_fiber_g: item.dietary_fiber_g,
        net_carbs_g: item.net_carbs_g,
        sugar_g: (item as any).sugar_g ?? 0,
        trans_fat_g: item.trans_fat_g ?? 0,
        saturated_fat_g: item.saturated_fat_g ?? 0,
        GI_estimate: item.GI_estimate,
        GL_estimate: item.GL_estimate,
        serving_description: item.serving_description,
      };
      });
    },
    [currentUploadedImage, uploadedImage]
  );

  const [foodItems, setFoodItems] = useState<FoodItem[]>(() => {
    // Helper to apply pending edits from localStorage
    const applyPendingEdits = (items: FoodItem[]): FoodItem[] => {
      try {
        const saved = localStorage.getItem("glucera_pending_edits");
        if (!saved) return items;
        const editsMap: Record<string, Partial<FoodItem>> = JSON.parse(saved);
        if (Object.keys(editsMap).length === 0) return items;
        return items.map(item => {
          const edit = editsMap[item.id];
          return edit ? { ...item, ...edit } : item;
        });
      } catch (_) {
        return items;
      }
    };

    // ALWAYS prefer fresh API data over localStorage
    if (foodItemsFromBackend && foodItemsFromBackend.length > 0) {
      const convertedItems = convertApiFoodItems(foodItemsFromBackend);
      return applyPendingEdits(convertedItems);
    }

    // Fallback to localStorage only if no API data
    const savedItems = localStorage.getItem("glucera_food_items");
    if (savedItems) {
      try {
        const parsedItems = JSON.parse(savedItems);

        // Update food items with current saved image
        const savedImage = localStorage.getItem("glucera_uploaded_image");

        if (savedImage && parsedItems.length > 0) {
          const updatedItems = parsedItems.map((item: FoodItem) => ({
            ...item,
            image: savedImage,
          }));
          return updatedItems;
        }

        return parsedItems;
      } catch (_error) {
        // Error parsing saved items
      }
    }

    // Return empty array if no API data
    return [];
  });

  const [selectedFoodItem, setSelectedFoodItem] = useState<FoodItem | null>(
    null
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveAsFavorite, setSaveAsFavorite] = useState(false);
  const [nutritionTotals, setNutritionTotals] =
    useState<NutritionTotals | null>(null);
  const [familyGroups, setFamilyGroups] = useState<{ id: string; name: string; member_count?: number; members?: { user_id: string; name: string; initial?: string; isYou?: boolean }[] }[]>([]);
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(new Set());
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set());
  const [membersLoadingIds, setMembersLoadingIds] = useState<Set<string>>(new Set());
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const [mealType, setMealType] = useState<string>(() => {
    try {
      const stored = localStorage.getItem("glucera_analyze_response");
      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, unknown>;
        const raw = readMealTypeFromAnalyzePayload(parsed);
        return toMealTypeSelectValue(raw);
      }
    } catch {
      /* ignore */
    }
    return "Breakfast";
  });

  const [mealTitle, setMealTitle] = useState<string | null>(() => {
    try {
      const stored = localStorage.getItem("glucera_analyze_response");
      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, unknown>;
        return readMealTitleFromAnalyzePayload(parsed);
      }
    } catch {
      /* ignore */
    }
    return null;
  });

  // Fetch family groups to display in Save Meal section
  useEffect(() => {
    FamilyService.getInstance().getGroups().then(res => {
      if (res?.groups?.length) {
        setFamilyGroups(
          res.groups
            .filter((g: any) => g.name)
            .map((g: any) => ({ id: g.id ?? g.group_id, name: g.name, member_count: g.member_count }))
        );
      }
    }).catch(() => {/* silently ignore */});
  }, []);

  // Fetch members for a group when it is expanded
  const handleToggleGroup = useCallback(async (groupId: string) => {
    setExpandedGroupIds(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) { next.delete(groupId); return next; }
      next.add(groupId);
      return next;
    });
    // Load members if not yet loaded
    setFamilyGroups(prev => {
      const existing = prev.find(g => g.id === groupId);
      if (existing?.members) return prev; // already loaded
      return prev;
    });
    setFamilyGroups(prevGroups => {
      const existing = prevGroups.find(g => g.id === groupId);
      if (existing?.members) return prevGroups;
      // Mark loading
      setMembersLoadingIds(l => { const n = new Set(l); n.add(groupId); return n; });
      const today = new Date().toISOString().split("T")[0];
      homeService.getHomePageData(today, groupId).then((data: any) => {
        const groups: any[] = data?.["Family Health"]?.groups ?? [];
        const found = groups.find((g: any) =>
          (g.id ?? g.group_id ?? g.family_group_id ?? "").replace(/-/g,"").toLowerCase() === groupId.replace(/-/g,"").toLowerCase()
        ) ?? groups[0];
        const rawMembers: any[] = found?.members ?? [];
        const members = rawMembers.map((m: any) => {
          const displayName = m.display_name ?? m.name ?? m.member_name ?? "Member";
          return {
            user_id: m.user_id ?? m.id ?? "",
            name: displayName,
            initial: m.initial ?? displayName[0]?.toUpperCase() ?? "?",
            isYou: !!(m.is_current_user ?? m.isYou),
          };
        });
        setFamilyGroups(pg => pg.map(g => g.id === groupId ? { ...g, members } : g));
        setMembersLoadingIds(l => { const n = new Set(l); n.delete(groupId); return n; });
      }).catch(() => {
        setMembersLoadingIds(l => { const n = new Set(l); n.delete(groupId); return n; });
      });
      return prevGroups;
    });
  }, []);

  const handleToggleMember = useCallback((userId: string) => {
    setSelectedMemberIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  }, []);

  // Recover glucose spike details from cached analyze response on refresh.
  useEffect(() => {
    const storedResponse = localStorage.getItem("glucera_analyze_response");
    if (!storedResponse) return;

    try {
      const parsed = JSON.parse(storedResponse) as Record<string, unknown>;
      if (!glucoseSpikeData) {
        const spikeFromApi =
          parsed?.glucose_spike ??
          (parsed?.data as Record<string, unknown> | undefined)?.glucose_spike ??
          (parsed?.result as Record<string, unknown> | undefined)?.glucose_spike;

        if (spikeFromApi) {
          // glucose_curve lives inside glucose_spike — use it directly from there.
          // Also accept a separately stored root-level glucose_curve as fallback.
          const curveFromRoot =
            parsed?.glucose_curve ??
            (parsed?.data as Record<string, unknown> | undefined)?.glucose_curve ??
            (parsed?.result as Record<string, unknown> | undefined)?.glucose_curve;

          const mergedSpike = {
            ...spikeFromApi,
            glucose_curve: spikeFromApi.glucose_curve ?? curveFromRoot ?? undefined,
          };

          setGlucoseSpikeData(mergedSpike);
        }
      }

      const rawBbsScore =
        parsed?.bbs_score ??
        (parsed?.data as Record<string, unknown> | undefined)?.bbs_score ??
        (parsed?.result as Record<string, unknown> | undefined)?.bbs_score ??
        (parsed?.glucose_spike as Record<string, unknown> | undefined)?.bbs_score ??
        ((parsed?.data as Record<string, unknown> | undefined)?.glucose_spike as Record<string, unknown> | undefined)?.bbs_score;
      const parsedBbsScore =
        typeof rawBbsScore === "string"
          ? Number(rawBbsScore)
          : rawBbsScore;
      if (typeof parsedBbsScore === "number" && !Number.isNaN(parsedBbsScore)) {
        setBbsScore(parsedBbsScore);
      }

      const rawBbsLabel =
        parsed?.bbs_label ??
        (parsed?.data as Record<string, unknown> | undefined)?.bbs_label ??
        (parsed?.result as Record<string, unknown> | undefined)?.bbs_label ??
        (parsed?.glucose_spike as Record<string, unknown> | undefined)?.bbs_label ??
        ((parsed?.data as Record<string, unknown> | undefined)?.glucose_spike as Record<string, unknown> | undefined)?.bbs_label;
      if (typeof rawBbsLabel === "string") {
        setBbsLabel(rawBbsLabel);
      }

      // Also recover meal_title and meal_type from cached response (same paths as initial state)
      const titleRaw = readMealTitleFromAnalyzePayload(parsed);
      if (titleRaw) {
        setMealTitle(titleRaw);
      }
      const typeRaw = readMealTypeFromAnalyzePayload(parsed);
      if (typeRaw) {
        setMealType(toMealTypeSelectValue(typeRaw));
      }
    } catch {
      // Ignore malformed cache values.
    }
  }, [glucoseSpikeData, setGlucoseSpikeData]);

  const todayLocal = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const da = String(d.getDate()).padStart(2, "0");
    return `${y}-${mo}-${da}`;
  }, []);

  const { data: homeDataForNames } = useQuery({
    queryKey: ["scannedDetailHomeExtras", todayLocal],
    queryFn: () => homeService.getHomePageData(todayLocal),
    staleTime: 60 * 1000,
  });

  const [editModalItem, setEditModalItem] = useState<FoodItem | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Persists local edits across navigation (edit → add new → etc.) so API refresh never overwrites them
  const localEditsRef = useRef<Map<number, Partial<FoodItem>>>(new Map());
  // Persists manually added items (not from API) so they survive foodItemsFromBackend effect rerun
  const localAddedItemsRef = useRef<FoodItem[]>([]);

  // On mount: restore any saved edits from localStorage so remount after navigation doesn't lose them
  useEffect(() => {
    try {
      const saved = localStorage.getItem("glucera_pending_edits");
      if (saved) {
        const editsMap: Record<string, Partial<FoodItem>> = JSON.parse(saved);
        Object.entries(editsMap).forEach(([id, edit]) => {
          localEditsRef.current.set(Number(id), edit);
        });
      }
    } catch (_) {}

    try {
      const savedAdded = localStorage.getItem("glucera_added_items");
      if (savedAdded) {
        localAddedItemsRef.current = JSON.parse(savedAdded);
      }
    } catch (_) {}
  }, []);

  // NOTE: mealId removed as add API is no longer used

  // On page load, ensure food items have correct images
  useEffect(() => {
    const savedImage = localStorage.getItem("glucera_uploaded_image");
    const savedItems = localStorage.getItem("glucera_food_items");

    // If we have saved image but food items don't have it, update them
    if (savedImage && savedItems) {
      try {
        const parsedItems = JSON.parse(savedItems);
        const needsUpdate = parsedItems.some(
          (item: FoodItem) => item.image !== savedImage
        );

        if (needsUpdate) {
          const updatedItems = parsedItems.map((item: FoodItem) => ({
            ...item,
            image: savedImage,
          }));
          setFoodItems(updatedItems);
          try {
            // Store only last 50 items to prevent quota issues
            const MAX_ITEMS = 50;
            const limitedItems = updatedItems.slice(0, MAX_ITEMS);
            localStorage.setItem(
              "glucera_food_items",
              JSON.stringify(limitedItems)
            );
          } catch (storageError) {
            // Quota exceeded - clear old data and retry
            console.warn("localStorage quota exceeded, clearing old data");
            localStorage.removeItem("glucera_food_items");
          }
        }
      } catch (error) {
        // Error parsing saved items on page load
      }
    }
  }, []); // Run only once on component mount

  // Track documentId changes
  useEffect(() => {
    // Document ID tracking
  }, [documentId]);

  // Convert blob URL to base64 and save to localStorage
  useEffect(() => {
    if (uploadedImage?.startsWith("blob:")) {
      // Convert blob URL to base64 data URL
      fetch(uploadedImage)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.blob();
        })
        .then(blob => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64String = reader.result as string;
            localStorage.setItem("glucera_uploaded_image", base64String);
            setCurrentUploadedImage(base64String);
          };
          reader.onerror = _error => {
            // Fallback - save the blob URL anyway
            localStorage.setItem("glucera_uploaded_image", uploadedImage);
            setCurrentUploadedImage(uploadedImage);
          };
          reader.readAsDataURL(blob);
        })
        .catch(_error => {
          // Fallback - save the blob URL anyway (though it may not work after refresh)
          localStorage.setItem("glucera_uploaded_image", uploadedImage);
          setCurrentUploadedImage(uploadedImage);
        });
    } else if (uploadedImage) {
      // If it's already a data URL or other format, save directly
      localStorage.setItem("glucera_uploaded_image", uploadedImage);
      setCurrentUploadedImage(uploadedImage);
    }
  }, [uploadedImage]);

  // Force update food items when currentUploadedImage changes
  useEffect(() => {
    const imageToUse = currentUploadedImage ?? uploadedImage;
    if (imageToUse && foodItems.length > 0) {
      setFoodItems(prevItems =>
        prevItems.map(item => ({
          ...item,
          image: imageToUse,
        }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUploadedImage, uploadedImage]);

  // Update food items when API data changes
  useEffect(() => {
    if (foodItemsFromBackend && foodItemsFromBackend.length > 0) {
      let convertedItems = convertApiFoodItems(foodItemsFromBackend);

      // Always apply ALL persisted local edits on top of fresh API data
      // This ensures edits survive even after adding new items or any re-render
      if (localEditsRef.current.size > 0) {
        convertedItems = convertedItems.map(item => {
          const localEdit = localEditsRef.current.get(item.id);
          return localEdit ? { ...item, ...localEdit } : item;
        });
      }

      // Apply any pending edit from EditFoodPage (via sessionStorage - race condition safe)
      const pendingEditRaw = sessionStorage.getItem("glucera_pending_food_edit");
      if (pendingEditRaw) {
        try {
          const pendingEdit = JSON.parse(pendingEditRaw);
          sessionStorage.removeItem("glucera_pending_food_edit");
          convertedItems = convertedItems.map(item =>
            item.id === pendingEdit.id ? { ...item, ...pendingEdit } : item
          );
          // Also persist in ref so future re-renders don't overwrite
          localEditsRef.current.set(pendingEdit.id, pendingEdit);
        } catch (_e) {
          sessionStorage.removeItem("glucera_pending_food_edit");
        }
      }

      // Merge: API items (with edits) + manually added items at the END
      const mergedItems = [...convertedItems, ...localAddedItemsRef.current];
      
      setFoodItems(mergedItems);
      setSelectedFoodItem(prev => {
        if (prev) {
          const localEdit = localEditsRef.current.get(prev.id);
          if (localEdit) return { ...prev, ...localEdit };
        }
        return mergedItems[0] ?? null;
      });

      // Save to localStorage for persistence
      try {
        const MAX_ITEMS = 50;
        const limitedItems = mergedItems.slice(0, MAX_ITEMS);
        localStorage.setItem(
          "glucera_food_items",
          JSON.stringify(limitedItems)
        );
      } catch (storageError) {
        console.warn("localStorage quota exceeded, skipping save");
        localStorage.removeItem("glucera_food_items");
      }
    } else if (localAddedItemsRef.current.length > 0) {
      // If no API data but have added items, show only added items
      setFoodItems(localAddedItemsRef.current);
      setSelectedFoodItem(localAddedItemsRef.current[localAddedItemsRef.current.length - 1] ?? null);
    }
  }, [foodItemsFromBackend, convertApiFoodItems]);

  // Calculate nutrition totals
  const calculateTotals = useCallback(() => {
    if (foodItems.length === 0) {
      setNutritionTotals(null);
      return;
    }

    const totals = foodItems.reduce(
      (acc, item) => ({
        carbohydrate_content_g:
          acc.carbohydrate_content_g + (item.carbohydrate_content_g ?? 0),
        dietary_fiber_g: acc.dietary_fiber_g + (item.dietary_fiber_g ?? 0),
        net_carbs_g: acc.net_carbs_g + (item.net_carbs_g ?? 0),
        sugar_g: acc.sugar_g + (item.sugar_g ?? 0),
        protein_content_g:
          acc.protein_content_g + (item.protein_content_g ?? 0),
        fat_content_g: acc.fat_content_g + (item.fat_content_g ?? 0),
        trans_fat_g: acc.trans_fat_g + (item.trans_fat_g ?? 0),
        saturated_fat_g: acc.saturated_fat_g + (item.saturated_fat_g ?? 0),
        calorie_content_kcal:
          acc.calorie_content_kcal +
          (item.calorie_content_kcal || item.calories || 0),
      }),
      {
        carbohydrate_content_g: 0,
        dietary_fiber_g: 0,
        net_carbs_g: 0,
        sugar_g: 0,
        protein_content_g: 0,
        fat_content_g: 0,
        trans_fat_g: 0,
        saturated_fat_g: 0,
        calorie_content_kcal: 0,
      }
    );

    const { giUsed, glUsed } = readMealGiGlFromAnalyze(glucoseSpikeData);

    setNutritionTotals({
      ...totals,
      GI_estimate: giUsed ?? 0,
      GL_estimate: glUsed ?? 0,
    });
  }, [foodItems, glucoseSpikeData]);

  // Update totals when food items change
  useEffect(() => {
    calculateTotals();
  }, [calculateTotals]);

  // Track food items changes for debugging
  useEffect(() => {
    // Save to localStorage whenever items change
    if (foodItems.length > 0) {
      // Ensure all items have the current uploaded image
      const itemsWithCurrentImage = foodItems.map(item => ({
        ...item,
        image: currentUploadedImage ?? item.image,
      }));

      try {
        // Store only last 50 items to prevent quota issues
        const MAX_ITEMS = 50;
        const limitedItems = itemsWithCurrentImage.slice(0, MAX_ITEMS);
        localStorage.setItem(
          "glucera_food_items",
          JSON.stringify(limitedItems)
        );
      } catch (storageError) {
        // Quota exceeded - clear old data
        console.warn("localStorage quota exceeded");
        localStorage.removeItem("glucera_food_items");
      }
    }
  }, [foodItems, currentUploadedImage]);

  // Toggle accordion item
  const toggleExpanded = (itemId: number) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // NOTE: Add API function removed as per requirement - all adds are local only
  // Helper function to add new food item via API - DISABLED
  /*
  const addFoodItemToApi = useCallback(
    async (newItem: FoodItem) => {
      // This function is no longer used - add operations are local only
      message.info("Add functionality is local only - no API calls made");
    },
    [mealId, uploadedImage]
  );
  */

  // Helper function to edit food item via API
  const editFoodItemInApi = useCallback(async (updatedItem: FoodItem) => {
    if (!updatedItem.food_item_id) {
      return;
    }

    try {
      const editedCount = updatedItem.count ?? updatedItem.servings ?? 1;
      const payload: EditFoodItemPayload = buildEditFoodPayload(
        updatedItem,
        updatedItem.name,
        editedCount
      );

      const response = await foodApiService.editFoodItem(
        updatedItem.food_item_id,
        payload
      );

      // Update the specific item in local state
      setFoodItems(prevItems =>
        prevItems.map(item =>
          item.food_item_id === updatedItem.food_item_id
            ? {
                ...item,
                name: response.data.food_items[0].name,
                calories: response.data.food_items[0].calorie_content_kcal,
                weight: response.data.food_items[0].serving_size_g,
                carbohydrate_content_g:
                  response.data.food_items[0].carbohydrate_content_g,
                protein_content_g:
                  response.data.food_items[0].protein_content_g,
                fat_content_g: response.data.food_items[0].fat_content_g,
                dietary_fiber_g: response.data.food_items[0].dietary_fiber_g,
                net_carbs_g: response.data.food_items[0].net_carbs_g,
                sugar_g: response.data.food_items[0].sugar_g,
                trans_fat_g: response.data.food_items[0].trans_fat_g,
                saturated_fat_g: response.data.food_items[0].saturated_fat_g,
                GI_estimate: response.data.food_items[0].GI_estimate,
                GL_estimate: response.data.food_items[0].GL_estimate,
                serving_description:
                  response.data.food_items[0].serving_description,
              }
            : item
        )
      );

      // Update selected item if it's the one being edited
      setSelectedFoodItem(prevSelectedItem => {
        if (prevSelectedItem?.food_item_id === updatedItem.food_item_id) {
          return {
            ...prevSelectedItem,
            name: response.data.food_items[0].name,
            calories: response.data.food_items[0].calorie_content_kcal,
            weight: response.data.food_items[0].serving_size_g,
            carbohydrate_content_g:
              response.data.food_items[0].carbohydrate_content_g,
            protein_content_g: response.data.food_items[0].protein_content_g,
            fat_content_g: response.data.food_items[0].fat_content_g,
            dietary_fiber_g: response.data.food_items[0].dietary_fiber_g,
            net_carbs_g: response.data.food_items[0].net_carbs_g,
            sugar_g: response.data.food_items[0].sugar_g,
            trans_fat_g: response.data.food_items[0].trans_fat_g,
            saturated_fat_g: response.data.food_items[0].saturated_fat_g,
            GI_estimate: response.data.food_items[0].GI_estimate,
            GL_estimate: response.data.food_items[0].GL_estimate,
            serving_description:
              response.data.food_items[0].serving_description,
          } as FoodItem;
        }
        return prevSelectedItem;
      });

      message.success(response.data.message);
    } catch (_error) {
      message.error("Failed to edit food item. Please try again.");
    }
  }, []);

  const handleEditItem = (item: FoodItem) => {
    // Open edit as modal — no navigation, no state loss
    setEditModalItem({ ...item, image: currentUploadedImage ?? uploadedImage ?? item.image });
  };

  const handleModalSave = (updatedItem: FoodItem) => {
    // Persist in ref so future API re-renders don't overwrite
    localEditsRef.current.set(updatedItem.id, updatedItem);

    // Also persist to localStorage so edits survive page remount (navigation away + back)
    try {
      const saved = localStorage.getItem("glucera_pending_edits");
      const editsMap = saved ? JSON.parse(saved) : {};
      editsMap[updatedItem.id] = updatedItem;
      localStorage.setItem("glucera_pending_edits", JSON.stringify(editsMap));
    } catch (_) {}

    // Update food items list
    setFoodItems(prev =>
      prev.map(item => item.id === updatedItem.id ? { ...item, ...updatedItem } : item)
    );
    // Update selected item if it's the one being edited
    setSelectedFoodItem(prev =>
      prev?.id === updatedItem.id ? { ...prev, ...updatedItem } : prev
    );
    setEditModalItem(null);
  };

  const handleDeleteItem = async (itemId: number) => {
    const itemToDelete = foodItems.find(item => item.id === itemId);

    if (!itemToDelete?.food_item_id) {
      // If no API ID, just remove from local state (for locally added items)
      setFoodItems(foodItems.filter(item => item.id !== itemId));
      
      // Also remove from localAddedItemsRef and localStorage
      localAddedItemsRef.current = localAddedItemsRef.current.filter(item => item.id !== itemId);
      try {
        localStorage.setItem("glucera_added_items", JSON.stringify(localAddedItemsRef.current));
      } catch (_) {}
      
      if (selectedFoodItem?.id === itemId) {
        setSelectedFoodItem(foodItems.find(item => item.id !== itemId) ?? null);
      }
      return;
    }

    try {
      // Call API to delete the item
      const response = await foodApiService.deleteFoodItem(
        itemToDelete.food_item_id
      );

      // Update local state with API response
      const updatedItems = response.data.food_items.map((apiItem, index) => ({
        id: index + 1,
        food_item_id: apiItem.food_item_id,
        name: apiItem.name,
        calories: apiItem.calorie_content_kcal,
        weight: apiItem.serving_size_g,
        servings: (apiItem as any).count ?? 1,
        image: currentUploadedImage ?? uploadedImage ?? ImageDosa,
        carbohydrate_content_g: apiItem.carbohydrate_content_g,
        protein_content_g: apiItem.protein_content_g,
        fat_content_g: apiItem.fat_content_g,
        dietary_fiber_g: apiItem.dietary_fiber_g,
        net_carbs_g: apiItem.net_carbs_g,
        GI_estimate: apiItem.GI_estimate,
        GL_estimate: apiItem.GL_estimate,
        serving_description: apiItem.serving_description,
      }));

      setFoodItems(updatedItems);

      // Update selected item
      if (selectedFoodItem?.id === itemId) {
        setSelectedFoodItem(updatedItems[0] ?? null);
      }

      message.success(response.data.message);
    } catch (_error) {
      message.error("Failed to delete food item. Please try again.");
    }
  };

  // State to track processed navigation events to prevent infinite loops
  const [processedLocationKey, setProcessedLocationKey] = useState<
    string | null
  >(null);

  // Handle updated item from edit page and new item from add page
  useEffect(() => {
    // Prevent processing same location state multiple times
    if (processedLocationKey === location.key) {
      return;
    }

    const updatedItem = location.state?.updatedItem;
    const newItem = location.state?.newItem;
    const localUpdatedItem = location.state?.localUpdatedItem;

    // Only process if we have some data to work with
    if (!updatedItem && !newItem && !localUpdatedItem) {
      return;
    }

    // Mark this location as processed
    setProcessedLocationKey(location.key);

    // Handle local item updates (no API call needed)
    if (localUpdatedItem) {
      // Save this edit to the persistent ref so it survives future location.state changes
      localEditsRef.current.set(localUpdatedItem.id, localUpdatedItem);

      setFoodItems(prev =>
        prev.map(item =>
          item.id === localUpdatedItem.id
            ? { ...item, ...localUpdatedItem }
            : item
        )
      );

      // Update selected item if it's the one being edited
      setSelectedFoodItem(prev => {
        if (prev?.id === localUpdatedItem.id) {
          return { ...prev, ...localUpdatedItem };
        }
        return prev;
      });

      // message.success("Food item updated successfully!"); // Removed: Show message only when saving meal

      // Clear the state after a delay
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      }, 500);
    }

    // Handle API item updates
    if (updatedItem) {
      // Call API to update the item
      editFoodItemInApi(updatedItem);

      // Clear the state after a delay
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      }, 500);
    }

    // Handle new item addition - Always local, no API calls
    if (newItem) {
      // Save to ref and localStorage so it survives foodItemsFromBackend effect rerun
      localAddedItemsRef.current.push(newItem);
      try {
        localStorage.setItem("glucera_added_items", JSON.stringify(localAddedItemsRef.current));
      } catch (_) {}

      // Use functional setState to always work with latest state (avoids stale closure)
      setFoodItems(prev => [...prev, newItem]);
      setSelectedFoodItem(newItem);
      // message.success(`Added "${newItem.name}" successfully!`); // Removed: Show message only when saving meal

      // Clear the state after a delay
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      }, 500);
    }
  }, [
    location.key,
    location.state,
    processedLocationKey,
    editFoodItemInApi,
  ]);

  const handleAddItem = () => {
    setIsAddModalOpen(true);
  };

  const handleAddModalSave = (newItem: FoodItem) => {
    // Save to ref and localStorage so it survives foodItemsFromBackend effect rerun
    localAddedItemsRef.current.push(newItem);
    try {
      localStorage.setItem("glucera_added_items", JSON.stringify(localAddedItemsRef.current));
    } catch (_) {}

    // Add to foodItems state
    setFoodItems(prev => [...prev, newItem]);
    setSelectedFoodItem(newItem);
    setIsAddModalOpen(false);
  };

  const handleSave = async () => {
    // Validate if we have food items to save
    if (foodItems.length === 0) {
      message.error("No food items to save!");
      return;
    }

    // Validate if we have nutritionTotals
    if (!nutritionTotals) {
      message.error("Nutrition totals not calculated!");
      return;
    }

    // Handle document_id - required for API
    let finalDocumentId: string;

    if (!documentId) {
      // Check if we have any items with food_item_id (from scanning)
      const hasScannedItems = foodItems.some(item => item.food_item_id);

      if (hasScannedItems) {
        // If we have scanned items but no documentId, it's an error
        message.error(
          "Document ID missing for scanned items! Please try scanning again."
        );
        return;
      } else {
        // All items are manually added - generate UUID for local storage only
        finalDocumentId = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
          /[xy]/g,
          function (c) {
            const r = (Math.random() * 16) | 0;
            const v = c === "x" ? r : (r & 0x3) | 0x8;
            return v.toString(16);
          }
        );

        // For manual entries, save locally and skip API call
        // message.success({
        //   content: `Saved ${foodItems.length} item${foodItems.length !== 1 ? "s" : ""} locally!`,
        //   duration: 3,
        // });

        // Save locally
        localStorage.setItem(
          `glucera_manual_meal_${Date.now()}`,
          JSON.stringify({
            foodItems,
            nutritionTotals,
            timestamp: new Date().toISOString(),
            documentId: finalDocumentId,
          })
        );

        // Clear temporary localStorage
        localStorage.removeItem("glucera_food_items");
        localStorage.removeItem("glucera_uploaded_image");
        localStorage.removeItem("glucera_document_id"); // Clear document ID for manual entries too

        // Navigate to meal page
        setTimeout(() => {
          setIsSaving(false);
          navigate(`/glucera${GluceraNavigationRoutes.Mealpage}`);
        }, 1000);

        return; // Exit early for manual entries
      }
    } else {
      // Use the existing document ID from scanning
      finalDocumentId = documentId;
    }

    // Set saving state
    setIsSaving(true);

    try {
      // Prepare payload for save meal API
      // First, try to get full API response from localStorage
      const storedResponse = localStorage.getItem("glucera_analyze_response");
      let dateString = localStorage.getItem("glucera_meal_date");
      
      // If we have stored response, extract date from it
      let foodAnalyzeData: Record<string, any> | undefined;
      if (storedResponse) {
        try {
          const parsedResponse = JSON.parse(storedResponse);
          if (!dateString) dateString = parsedResponse.date;
          foodAnalyzeData = parsedResponse;
        } catch (e) {
          console.error("❌ Error parsing stored response:", e);
        }
      }

      // If no date in localStorage, create date string manually
      if (!dateString) {
        // Create date string in format: "2025-11-05 17:47:08" using UTC time
        // Backend expects UTC time, so we send UTC to match what backend returns
        const currentDate = new Date();

        // Format: YYYY-MM-DD HH:mm:ss using UTC time (to match backend)
        const year = currentDate.getUTCFullYear();
        const month = String(currentDate.getUTCMonth() + 1).padStart(2, "0");
        const day = String(currentDate.getUTCDate()).padStart(2, "0");
        const hours = String(currentDate.getUTCHours()).padStart(2, "0");
        const minutes = String(currentDate.getUTCMinutes()).padStart(2, "0");
        const seconds = String(currentDate.getUTCSeconds()).padStart(2, "0");

        dateString = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      }

      const currentUserId = String(
        decodedToken?.user_id ?? decodedToken?.userId ?? "",
      ).trim();
      const isFamilySave = selectedMemberIds.size > 0;
      const logForUserIds = buildLogForUserIds(currentUserId, selectedMemberIds);
      const selectedFamilyGroups = isFamilySave
        ? buildSelectedFamilyGroups(familyGroups, selectedMemberIds, currentUserId)
        : [];
      const selectedFamilyGroupId =
        selectedFamilyGroups.length === 1 ? selectedFamilyGroups[0].group_id : undefined;
      const { giUsed, glUsed } = readMealGiGlFromAnalyze(glucoseSpikeData);
      const sanitizedAnalyzeData = buildFoodAnalyzeDataForSave(foodAnalyzeData, {
        logForUserIds,
        requireApproval: isFamilySave,
        familyGroupId: selectedFamilyGroupId,
        familyGroups: selectedFamilyGroups.length ? selectedFamilyGroups : undefined,
        giUsed,
        glUsed,
      });
      const mealTitleForSave = resolveMealTitleForSave({
        mealTitle,
        analyzeData: foodAnalyzeData,
        mealType,
        foodItems,
      });

      const payload: SaveMealPayload = {
        ...(logForUserIds?.length ? { log_for_user_ids: logForUserIds } : {}),
        require_approval: isFamilySave,
        ...(selectedFamilyGroupId && { family_group_id: selectedFamilyGroupId }),
        document_id: finalDocumentId,
        date: dateString,
        meal_type: mealType,
        ...(mealTitleForSave && { meal_title: mealTitleForSave }),
        ...(ghiScore !== null && { ghi_score: ghiScore }),
        ...(ghiLabel && { ghi_label: ghiLabel }),
        ...(ghiReasons && ghiReasons.length > 0 && { ghi_reasons: ghiReasons }),
        ...(otherDetails && { other_details: otherDetails }),
        ...(sanitizedAnalyzeData && { food_analyze_data: sanitizedAnalyzeData }),
        food_items: foodItems.map(item => {
          return {
            name: item.name,
            csv_name:
              item.csv_name ?? item.name.toLowerCase().replace(/\s+/g, "_"), // Backend field
            source: item.source ?? "llm", // Backend field: llm, csv, etc.
            count: item.count ?? 1, // Backend field: item count
            serving_size_g: Number(item.weight), // Ensure it's a number
            serving_description:
              item.serving_description ?? `${item.weight}g serving`,
            carbohydrate_content_g: Number(item.carbohydrate_content_g ?? 0),
            dietary_fiber_g: Number(item.dietary_fiber_g ?? 0),
            net_carbs_g: Number(item.net_carbs_g ?? 0),
            sugar_g: Number(item.sugar_g ?? 0),
            protein_content_g: Number(item.protein_content_g ?? 0),
            fat_content_g: Number(item.fat_content_g ?? 0),
            trans_fat_g: Number(item.trans_fat_g ?? 0),
            saturated_fat_g: Number(item.saturated_fat_g ?? 0),
            calorie_content_kcal: Number(item.calories),
            GI_estimate: Number(item.GI_estimate ?? 0),
            GL_estimate: Number(item.GL_estimate ?? 0),
          };
        }),
        totals: {
          carbohydrate_content_g: Number(
            nutritionTotals.carbohydrate_content_g
          ),
          dietary_fiber_g: Number(nutritionTotals.dietary_fiber_g),
          net_carbs_g: Number(nutritionTotals.net_carbs_g),
          sugar_g: Number(nutritionTotals.sugar_g ?? 0),
          protein_content_g: Number(nutritionTotals.protein_content_g),
          fat_content_g: Number(nutritionTotals.fat_content_g),
          trans_fat_g: Number(nutritionTotals.trans_fat_g ?? 0),
          saturated_fat_g: Number(nutritionTotals.saturated_fat_g ?? 0),
          calorie_content_kcal: Number(nutritionTotals.calorie_content_kcal),
          GI_estimate: giUsed ?? 0,
          GL_estimate: glUsed ?? 0,
        },
      };

      // Call save meal API
      const response = await foodApiService.saveMeal(payload);

      // Store last saved meal_data_id for intervention calculate API
      const savedMealId = response.data?.meal_data_id ?? response.data?.meal_id;
      if (savedMealId) {
        localStorage.setItem("glucera_last_meal_data_id", savedMealId);
        if (saveAsFavorite) {
          try {
            await favoriteMealService.markFavorite(savedMealId);
          } catch {
            message.warning("Meal saved, but it could not be added to favourites.");
          }
        }
      }

      // Check if current user is in pending approvals list with is_title true
      const userHasPendingApproval = response.data.pending_approvals?.some(
        (approval) =>
          approval.recipient_user_id === currentUserId && approval.is_title === true,
      );

      // Navigate without showing success popup
      if (userHasPendingApproval) {
        Dialog.show({
          title: 'Pending Approval',
          content: response.data.message || 'Meal saved; other members were notified to approve.',
          closeOnAction: true,
          actions: [{ key: 'ok', text: 'OK' }],
        });
      }

      // Show success message
      // message.success({
      //   content:
      //     response.data.message ||
      //     `Successfully saved ${foodItems.length} food item${foodItems.length !== 1 ? "s" : ""}!`,
      //   duration: 3,
      // });

      // Clear localStorage after successful save
      localStorage.removeItem("glucera_food_items");
      localStorage.removeItem("glucera_uploaded_image");
      localStorage.removeItem("glucera_document_id"); // Clear document ID after successful save
      localStorage.removeItem("glucera_meal_date"); // Clear meal date after successful save
      localStorage.removeItem("glucera_analyze_response"); // Clear full API response after successful save
      localStorage.removeItem("glucera_pending_edits"); // Clear pending edits after successful save
      localStorage.removeItem("glucera_added_items"); // Clear added items after successful save

      // Clear local refs
      localEditsRef.current.clear();
      localAddedItemsRef.current = [];

      // Clear GHI context after successful save
      setGhiScore(null);
      setGhiLabel(null);
      setGhiReasons(null);
      setOtherDetails(null);
      setGlucoseSpikeData(null);

      // Update food items with returned food_item_ids from API
      if (response.data.food_items) {
        const updatedItems = response.data.food_items.map((apiItem, index) => ({
          id: index + 1,
          food_item_id: apiItem.food_item_id,
          name: apiItem.name,
          calories: apiItem.calorie_content_kcal,
          weight: apiItem.serving_size_g,
          servings: (apiItem as any).count ?? 1,
          image: currentUploadedImage ?? uploadedImage ?? ImageDosa,
          carbohydrate_content_g: apiItem.carbohydrate_content_g,
          protein_content_g: apiItem.protein_content_g,
          fat_content_g: apiItem.fat_content_g,
          dietary_fiber_g: apiItem.dietary_fiber_g,
          net_carbs_g: apiItem.net_carbs_g,
          sugar_g: apiItem.sugar_g,
          trans_fat_g: apiItem.trans_fat_g,
          saturated_fat_g: apiItem.saturated_fat_g,
          GI_estimate: apiItem.GI_estimate,
          GL_estimate: apiItem.GL_estimate,
          serving_description: apiItem.serving_description,
        }));

        setFoodItems(updatedItems);
      }

      // Invalidate homepage cache so fresh data loads when user goes back
      queryClient.invalidateQueries({ queryKey: ["homePageData"] });

      // Redirect to Meal page after a short delay
      setTimeout(() => {
        setIsSaving(false);
        navigate(`/glucera${GluceraNavigationRoutes.Mealpage}`);
      }, 1000);
    } catch (error: any) {
      setIsSaving(false);

      // Safe error message extraction
      let errorMessage = "Failed to save meal. Please try again.";

      try {
        if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error?.response?.data) {
          errorMessage =
            typeof error.response.data === "string"
              ? error.response.data
              : JSON.stringify(error.response.data);
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (error?.response?.statusText) {
          errorMessage = `API Error: ${error.response.statusText}`;
        }
      } catch (_parseError) {
        errorMessage = "Failed to save meal. Please try again.";
      }

      // Show error message
      message.error({
        content: errorMessage,
        duration: 4,
      });
    }
  };

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage:
            currentUploadedImage || uploadedImage
              ? `url(${currentUploadedImage || uploadedImage})`
              : `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-3 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <button
            onClick={handleBackNavigation}
            className="rounded-xl bg-card/80 p-2 shadow-sm backdrop-blur-sm hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1 text-center">
            <h1 className={gluceraReviewTitle}>Review Meal</h1>
            {fromHomePage && (
              <p className={gluceraReviewCaption}>Meal {mealNumber}</p>
            )}
          </div>
          <div className="w-9" />
        </div>

        <div className="space-y-3">
          {/* Food Image or No Image Message */}
          {(() => {
            const imageToShow = currentUploadedImage ?? uploadedImage;

            if (imageToShow) {
              return (
                <div className="overflow-hidden rounded-xl border border-border bg-card shadow-lg">
                  <div className="relative">
                    <img
                      src={imageToShow}
                      alt="Food"
                      className="h-52 w-full object-cover"
                      onError={_e => {}}
                    />
                    {/* Top-right: items detected badge */}
                    <div className="absolute right-2 top-2">
                      <div className={cn("rounded-lg bg-card/90 px-2 py-1 shadow-sm backdrop-blur-sm", gluceraMicroLabel, "normal-case tracking-normal font-semibold text-foreground")}>
                        {foodItems.length} items detected
                      </div>
                    </div>
                    {/* Bottom: meal title overlay */}
                    {mealTitle && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent px-3 pb-3 pt-6">
                        <p className="truncate text-callout font-bold text-white drop-shadow-sm">{mealTitle}</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            }

            return (
              <div className="overflow-hidden rounded-xl border border-border bg-card shadow-lg">
                <div className="relative">
                  <div className="flex h-52 items-center justify-center bg-muted">
                    <Camera className="h-16 w-16 text-muted-foreground" />
                  </div>
                  <div className="absolute right-2 top-2">
                    <div className={cn("rounded-lg bg-card/90 px-2 py-1 shadow-sm backdrop-blur-sm", gluceraMicroLabel, "normal-case tracking-normal font-semibold text-foreground")}>
                      {foodItems.length} items detected
                    </div>
                  </div>
                  {/* Bottom: meal title overlay for no-image state */}
                  {mealTitle && (
                    <div className="absolute bottom-0 left-0 right-0 border-t border-border bg-card/95 px-3 py-2 backdrop-blur-sm">
                      <p className="truncate text-subhead font-bold text-foreground">{mealTitle}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* GHI Score Card — shown between image and nutrition */}
          {foodItems.length > 0 && bbsScore !== null && (() => {
            const scoreColor =
              bbsLabel === "Excellent" ? { ring: "ring-green-400", pulse: "bg-green-400", circleBorder: "border-green-300 bg-green-100 dark:border-green-700 dark:bg-green-900/40", text: "text-green-700 dark:text-green-400", badge: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400", bar: "bg-green-500", dot: "bg-green-500", card: "border-green-200/70 bg-gradient-to-br from-green-50 to-emerald-50/60 dark:border-green-800/40 dark:from-green-950/30 dark:to-emerald-950/20" }
              : bbsLabel === "Good" ? { ring: "ring-emerald-400", pulse: "bg-emerald-400", circleBorder: "border-emerald-300 bg-emerald-100 dark:border-emerald-700 dark:bg-emerald-900/40", text: "text-emerald-700 dark:text-emerald-400", badge: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400", bar: "bg-emerald-500", dot: "bg-emerald-500", card: "border-emerald-200/70 bg-gradient-to-br from-emerald-50 to-teal-50/60 dark:border-emerald-800/40 dark:from-emerald-950/30 dark:to-teal-950/20" }
              : bbsLabel === "Fair" ? { ring: "ring-yellow-400", pulse: "bg-yellow-400", circleBorder: "border-yellow-300 bg-yellow-100 dark:border-yellow-700 dark:bg-yellow-900/40", text: "text-yellow-700 dark:text-yellow-400", badge: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-500", bar: "bg-yellow-500", dot: "bg-yellow-500", card: "border-yellow-200/70 bg-gradient-to-br from-yellow-50 to-amber-50/60 dark:border-yellow-800/40 dark:from-yellow-950/30 dark:to-amber-950/20" }
              : { ring: "ring-red-400", pulse: "bg-red-400", circleBorder: "border-red-300 bg-red-100 dark:border-red-700 dark:bg-red-900/40", text: "text-red-700 dark:text-red-400", badge: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400", bar: "bg-red-500", dot: "bg-red-500", card: "border-red-200/70 bg-gradient-to-br from-red-50 to-rose-50/60 dark:border-red-800/40 dark:from-red-950/30 dark:to-rose-950/20" };

            const displayedScore = Math.round(bbsScore);
            const displayedLabel = bbsLabel;

            return (
              <div className={`rounded-2xl border shadow-sm animate-[fadeSlideUp_0.5s_ease-out] ${scoreColor.card}`}>
                <div className="px-5 py-4">
                  {/* Top row - Score circle + label + badge */}
                  <div className="flex items-center gap-3">
                    {/* Animated Score circle with pulse ring */}
                    <div className="relative flex-shrink-0">
                      {/* Pulse ring */}
                      <div className={`absolute inset-0 rounded-full ${scoreColor.pulse} opacity-20 animate-ping`} />
                      <div className={`relative flex h-16 w-16 items-center justify-center rounded-full border-2 ring-2 ring-offset-2 ${scoreColor.circleBorder} ${scoreColor.ring} ring-offset-background`}>
                        <span className={cn(gluceraScoreDisplay, "animate-[countUp_0.8s_ease-out]", scoreColor.text)}>
                          {displayedScore ?? "—"}
                        </span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className={gluceraScoreEyebrow}>Bite Balance Score</p>
                        <span className="shrink-0 text-footnote text-muted-foreground">out of 100</span>
                      </div>
                      {displayedLabel && (
                        <span className={`mt-1.5 inline-flex items-center rounded-full px-2.5 py-1 text-footnote font-bold whitespace-nowrap ${scoreColor.badge}`}>
                          {displayedLabel}
                        </span>
                      )}
                      {/* Progress bar below label */}
                      <div className="mt-2.5 h-2.5 w-full overflow-hidden rounded-full bg-border/40">
                        <div
                          className={`h-full rounded-full ${scoreColor.bar}`}
                          style={{
                            width: `${Math.min(displayedScore ?? 0, 100)}%`,
                            animation: "expandWidth 1s ease-out forwards",
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  {ghiReasons && ghiReasons.length > 0 && (
                    <div className="my-3 border-t border-border/40" />
                  )}

                  {/* Reasons — staggered fade in */}
                  {ghiReasons && ghiReasons.length > 0 && (
                    <div className="space-y-1.5">
                      {ghiReasons.map((reason, index) => (
                        <div
                          key={index}
                          className="flex items-start gap-2"
                          style={{ animation: `fadeSlideUp 0.4s ease-out ${0.3 + index * 0.1}s both` }}
                        >
                          <div className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${scoreColor.dot}`} />
                          <p className={gluceraFootnoteBody}>{reason}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Predicted Glucose Spike */}
          {nutritionTotals && foodItems.length > 0 && (
            <>
              <PredictedGlucoseSpikeCard
                glucoseSpikeData={glucoseSpikeData ?? undefined}
                spikeValue={glucoseSpikeData?.predicted_spike_mg_dl}
                mealsLogged={1}
                variant="curve"
                showPredictedRange={true}
              />
              <MedicalDisclaimerNotice />
            </>
          )}

          {/* Plate Optimisation */}
          {(() => {
            let plateData: PlateOptimisationData | undefined;
            try {
              const raw = localStorage.getItem("glucera_analyze_response");
              if (raw) {
                const parsed = JSON.parse(raw);
                plateData = parsed?.plate_optimisation ?? parsed?.data?.plate_optimisation;
              }
            } catch { /* ignore */ }
            if (!plateData) {
              plateData = (homeDataForNames as any)?.plate_optimisation;
            }
            return plateData ? <PlateOptimisationCard plateData={plateData} /> : null;
          })()}

          {/* Total Nutrition Facts */}
          <NutritionFactsCard
            nutritionTotals={nutritionTotals}
            foodItems={foodItems}
          />

          {/* Food Items List */}
          <FoodItemsList
            foodItems={foodItems}
            uploadedImage={currentUploadedImage ?? uploadedImage}
            expandedItems={expandedItems}
            onToggleExpanded={toggleExpanded}
            onEditItem={handleEditItem}
            onDeleteItem={handleDeleteItem}
            onAddItem={handleAddItem}
          />

          {/* Save Meal Section */}
          <div className="space-y-4 rounded-2xl border border-border/70 bg-card p-4 shadow-lg">

            {/* Meal type — headline reflects API-detected type; user can adjust */}
            <div className="space-y-3">
              <div>
                <p className={cn(gluceraSectionTitle, "leading-snug")}>
                  {lookLikeMealHeadline(mealType)}
                </p>
                <p className={cn("mt-2", gluceraFootnoteBody)}>
                  Confirm what&apos;s on your plate. We&apos;ll predict your 2-hour
                  curve from this.
                </p>
              </div>
              <div className="space-y-1.5">
                <label
                  className={cn("block", gluceraFieldLabel, "text-caption1 font-medium text-muted-foreground")}
                  htmlFor="scanned-meal-type"
                >
                  Meal type
                </label>
                <select
                  id="scanned-meal-type"
                  className={cn(gluceraFormControl, "p-3 focus:border-blue-500 focus:ring-blue-200")}
                  value={mealType}
                  onChange={e => setMealType(e.target.value)}
                  aria-label="Meal type for this log"
                >
                  <option value="Breakfast">Breakfast</option>
                  <option value="Lunch">Lunch</option>
                  <option value="Snack">Snack</option>
                  <option value="Dinner">Dinner</option>
                </select>
              </div>
            </div>

            {/* Family Groups */}
            {familyGroups.length > 0 && (
              <div className="space-y-2">
                {/* Header with count */}
                {selectedMemberIds.size > 0 && (
                  <div className="flex items-center gap-2 rounded-lg bg-blue-50 dark:bg-blue-950/20 px-3 py-2 border border-blue-200 dark:border-blue-900/40">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500 text-caption2 font-bold text-white">
                      {selectedMemberIds.size}
                    </div>
                    <span className="text-caption1 font-medium text-blue-700 dark:text-blue-300">
                      {selectedMemberIds.size === 1
                        ? "1 member selected — meal will be sent for their approval"
                        : `${selectedMemberIds.size} members selected — meals will be sent for approval`}
                    </span>
                  </div>
                )}
                <p className="text-caption2 text-muted-foreground px-1">
                  Tap members to log for them (multiple allowed). Leave unselected to save only for yourself.
                </p>

                {/* Groups list */}
                <div className="space-y-2">
                  {familyGroups.map(group => {
                    const isExpanded = expandedGroupIds.has(group.id);
                    const isLoading = membersLoadingIds.has(group.id);
                    const members = group.members ?? [];
                    return (
                      <div key={group.id} className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
                        {/* Group header */}
                        <button
                          type="button"
                          onClick={() => handleToggleGroup(group.id)}
                          className="flex w-full items-center justify-between p-3 text-left transition-colors hover:bg-muted/50"
                        >
                          <div className="flex items-center gap-2.5">
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
                              <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                              <p className="text-subhead font-semibold text-foreground">{group.name}</p>
                              {group.member_count != null && (
                                <p className="text-caption2 text-muted-foreground">{group.member_count} members</p>
                              )}
                            </div>
                          </div>
                          <div className={`transition-transform ${isExpanded ? "rotate-180" : ""}`}>
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          </div>
                        </button>

                        {/* Members grid */}
                        {isExpanded && (
                          <div className="border-t border-border bg-muted/30 p-3">
                            {isLoading ? (
                              <div className="grid grid-cols-4 gap-3">
                                {[1,2,3,4].map(i => (
                                  <div key={i} className="flex flex-col items-center gap-1.5">
                                    <div className="h-12 w-12 animate-pulse rounded-full bg-muted" />
                                    <div className="h-2.5 w-full animate-pulse rounded bg-muted" />
                                  </div>
                                ))}
                              </div>
                            ) : members.length === 0 ? (
                              <p className="text-center text-caption1 text-muted-foreground py-2">No members found</p>
                            ) : (
                              <div className="grid grid-cols-4 gap-3">
                                {members.map(member => {
                                  const isSelected = selectedMemberIds.has(member.user_id);
                                  return (
                                    <button
                                      key={member.user_id}
                                      type="button"
                                      onClick={() => !member.isYou && handleToggleMember(member.user_id)}
                                      disabled={member.isYou}
                                      className={`flex flex-col items-center gap-1.5 rounded-lg p-2 transition-all ${
                                        member.isYou
                                          ? "cursor-default opacity-50"
                                          : isSelected
                                          ? "bg-blue-500 scale-95"
                                          : "hover:bg-muted active:scale-95"
                                      }`}
                                    >
                                      <div className={`relative flex h-12 w-12 items-center justify-center rounded-full text-subhead font-bold text-white transition-all ${
                                        isSelected
                                          ? "bg-white ring-2 ring-white"
                                          : member.isYou
                                          ? "bg-muted-foreground/40"
                                          : "bg-muted-foreground/60"
                                      }`}>
                                        {isSelected ? (
                                          <Check className="h-5 w-5 text-blue-500" />
                                        ) : (
                                          <span className={isSelected ? "text-blue-500" : ""}>
                                            {member.initial ?? member.name[0]}
                                          </span>
                                        )}
                                      </div>
                                      <span className={`w-full truncate text-center text-compact-sm font-medium leading-tight ${
                                        isSelected ? "text-white" : "text-foreground"
                                      }`}>
                                        {member.isYou ? "You" : member.name.split(" ")[0]}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={() => setSaveAsFavorite((current) => !current)}
              className={cn(
                "mb-2 flex w-full items-center justify-center gap-2 rounded-xl border px-5 py-3",
                saveAsFavorite
                  ? "border-red-200 bg-red-50 text-red-600"
                  : "border-border bg-card text-foreground",
              )}
            >
              <Heart className={cn("h-4 w-4", saveAsFavorite && "fill-current")} />
              <span className="text-footnote font-semibold">
                {saveAsFavorite ? "Save this meal as a favourite" : "Add to favourites"}
              </span>
            </button>
            <p className="mb-3 text-center text-footnote leading-snug text-muted-foreground">
              {saveAsFavorite
                ? "Next time you can log this meal in one tap, without a new photo."
                : "Keep meals you eat often here, so logging them later is faster."}
            </p>
            <button
              onClick={isSaving ? undefined : handleSave}
              disabled={isSaving}
              className={cn("w-full rounded-xl bg-gradient-primary px-5 py-3 text-primary-foreground shadow-primary transition-all hover:opacity-90 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:shadow-md dark:shadow-blue-900/30", gluceraPrimaryCta)}
            >
              <span className="flex items-center justify-center gap-2">
                {isSaving ? "Saving..." : "Save Meal"}
              </span>
            </button>
          </div>
        </div>
      </div>
      {/* Edit Food Modal */}
      {editModalItem && (
        <EditFoodModal
          foodItem={editModalItem}
          onClose={() => setEditModalItem(null)}
          onSave={handleModalSave}
        />
      )}

      {/* Add Food Modal */}
      <AddFoodModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddModalSave}
        uploadedImage={currentUploadedImage ?? uploadedImage}
      />
    </div>
  );
};

export default ScannedFooddetailPage;
